import { cn } from "@/shared/lib/utils";

/**
 * Apply a front-loaded perceptual curve so the bar appears to move
 * fast initially and slow near the end, then snap to done.
 *
 * Formula: displayed = actual^0.4  (exponent < 1 = front-loaded)
 *   10% actual → 40% displayed
 *   50% actual → 76% displayed
 *   90% actual → 96% displayed
 */
function weightedProgress(value: number): number {
  const clamped = Math.min(100, Math.max(0, value));
  if (clamped === 0 || clamped === 100) return clamped;
  return Math.pow(clamped / 100, 0.4) * 100;
}

interface ProgressBarProps {
  /** Actual progress 0-100 */
  value: number;
  /** Apply perceptual front-loaded curve (default: true) */
  weighted?: boolean;
  /** Show shimmer animation while incomplete (default: true) */
  shimmer?: boolean;
  /** Override bar fill styling (height, color, rounding) */
  className?: string;
}

export function ProgressBar({
  value,
  weighted = true,
  shimmer = true,
  className,
}: ProgressBarProps) {
  const displayed = weighted ? weightedProgress(value) : value;
  const isComplete = value >= 100;

  return (
    <div className="h-1 w-full bg-muted overflow-hidden">
      <div
        className={cn(
          "h-full bg-[var(--brand-teal)] transition-all duration-500 ease-out relative overflow-hidden",
          shimmer && !isComplete && "animate-progress-shimmer",
          className,
        )}
        style={{ width: `${displayed}%` }}
      />
    </div>
  );
}
